/**
 * JTL Service
 * JTL API entegrasyonu için servis sınıfı
 * Not: Backend JTL response'unu Shopify formatına çevirdiği için
 * bu service shopify.service.ts ile aynı interface'i kullanır
 */

import api from '../lib/api';
import { ShopifyOrder, ShopifyApiResponse, ShopifyOrderResponse } from './shopify.service';
import { formatDate } from '@/lib/format';

class JTLService {
  /**
   * Email adresine göre siparişleri getirir (JTL API'den)
   */
  async getOrdersByEmail(email: string, organizationId?: string): Promise<ShopifyApiResponse> {
    const response = await api.get<ShopifyApiResponse>(`/jtl/orders/${email}`, {
      params: {
        organizationId: organizationId
      }
    });
    return response.data;
  }

  /**
   * JTL order verilerini frontend için uygun formata çevirir
   * Not: Backend zaten çevirdiği için bu fonksiyon shopify.service.ts'dekiyle aynı
   */
  formatOrderForUI(order: ShopifyOrder) {
    return {
      orderNumber: order.orderNumber,
      confirmationNumber: order.confirmationNumber,
      externalOrderNumber: (order as any).externalOrderNumber,
      kAuftrag: (order as any).kAuftrag,
      date: formatDate(order.createdAt),
      paymentStatus: this.translatePaymentStatus(order.financialStatus),
      paymentMethod: (order as any).paymentMethod,
      orderType: (order as any).orderType,
      fulfillmentStatus: this.translateFulfillmentStatus(order.fulfillmentStatus),
      expectedDeliveryDate: (order as any).expectedDeliveryDate ? formatDate((order as any).expectedDeliveryDate) : null,
      deliveryDate: (order as any).deliveryDate ? formatDate((order as any).deliveryDate) : null,
      shippingCountry: (order as any).shippingCountry,
      total: `${order.totalPrice} ${order.currency}`,
      currency: order.currency,
      totalDiscounts: (order as any).totalDiscounts,
      items: order.lineItems.map(item => ({
        sku: item.sku,
        name: item.name,
        quantity: item.quantity,
        orderedQuantity: (item as any).orderedQuantity,
        shippedQuantity: (item as any).shippedQuantity,
        price: `${item.price} ${order.currency}`,
        vendor: item.vendor,
        discount: (item as any).discount,
        note: (item as any).note,
        unit: (item as any).unit,
        taxRate: (item as any).taxRate,
        tracking: (item as any).tracking
      })),
      customer: order.customer ? {
        id: order.customer.id.toString(),
        name: `${order.customer.firstName} ${order.customer.lastName}`,
        email: order.customer.email,
        phone: order.customer.phone,
        verified: order.customer.verified
      } : null,
      shippingAddress: {
        name: `${order.shippingAddress.first_name} ${order.shippingAddress.last_name}`,
        company: order.shippingAddress.company,
        address1: order.shippingAddress.address1,
        address2: order.shippingAddress.address2,
        city: order.shippingAddress.city,
        zip: order.shippingAddress.zip,
        country: order.shippingAddress.country,
        phone: order.shippingAddress.phone,
        mobile: (order.shippingAddress as any).mobile,
        fullAddress: `${order.shippingAddress.address1}${order.shippingAddress.address2 ? ', ' + order.shippingAddress.address2 : ''}, ${order.shippingAddress.zip} ${order.shippingAddress.city}, ${order.shippingAddress.country}`
      },
      billingAddress: {
        name: `${order.billingAddress.first_name} ${order.billingAddress.last_name}`,
        company: order.billingAddress.company,
        address1: order.billingAddress.address1,
        address2: order.billingAddress.address2,
        city: order.billingAddress.city,
        zip: order.billingAddress.zip,
        country: order.billingAddress.country,
        phone: order.billingAddress.phone,
        mobile: (order.billingAddress as any).mobile,
        fullAddress: `${order.billingAddress.address1}${order.billingAddress.address2 ? ', ' + order.billingAddress.address2 : ''}, ${order.billingAddress.zip} ${order.billingAddress.city}, ${order.billingAddress.country}`
      },
      trackingNumbers: this.extractTrackingNumbers(order),
      discounts: order.discountCodes,
      returns: (order as any).returns,
      shopType: 'JTL Shop',
      orderStatusUrl: order.orderStatusUrl,
      adminUrl: order.adminUrl
    };
  }

  /**
   * Payment status'u Almancaya çevirir
   */
  private translatePaymentStatus(status: string): string {
    const statusMap: { [key: string]: string } = {
      'paid': 'Bezahlt',
      'pending': 'Ausstehend',
      'authorized': 'Autorisiert',
      'partially_paid': 'Teilweise bezahlt',
      'refunded': 'Rückerstattet',
      'voided': 'Storniert',
      'partially_refunded': 'Teilweise rückerstattet'
    };
    return statusMap[status] || status;
  }

  /**
   * Fulfillment status'u Almancaya çevirir
   */
  private translateFulfillmentStatus(status: string | null): string {
    if (!status) return 'Ausstehend';

    const statusMap: { [key: string]: string } = {
      'fulfilled': 'Versandt',
      'partial': 'Teilweise versandt',
      'restocked': 'Wieder eingelagert',
      'unfulfilled': 'Nicht versandt',
      'pending': 'Ausstehend',
      'cancelled': 'Storniert'
    };
    return statusMap[status] || status;
  }

  /**
   * Fulfillment bilgilerinden tracking numaralarını çıkarır
   */
  private extractTrackingNumbers(order: ShopifyOrder): Array<{
    number?: string;
    url?: string;
    carrier?: string;
    TrackingNumber?: string;
    trackingUrl?: string;
    CarrierName?: string;
    ShippedDate?: string;
    ShippingStatus?: number;
    Weight?: number;
    DeliveryNoteNumber?: string;
    DeliveryNoteDate?: string;
  }> {
    const trackingNumbers: Array<any> = [];

    if (order.fulfillments && order.fulfillments.length > 0) {
      order.fulfillments.forEach((fulfillment: any) => {
        if (fulfillment.tracking_numbers && fulfillment.tracking_numbers.length > 0) {
          fulfillment.tracking_numbers.forEach((trackingNumber: string, index: number) => {
            trackingNumbers.push({
              number: trackingNumber,
              TrackingNumber: trackingNumber,
              url: fulfillment.tracking_urls?.[index] || undefined,
              trackingUrl: fulfillment.tracking_urls?.[index] || undefined,
              carrier: fulfillment.tracking_company || undefined,
              CarrierName: fulfillment.tracking_company || undefined,
              ShippedDate: fulfillment.shipped_date,
              ShippingStatus: fulfillment.shipping_status,
              Weight: fulfillment.weight,
              DeliveryNoteNumber: fulfillment.delivery_note_number,
              DeliveryNoteDate: fulfillment.delivery_note_date
            });
          });
        }
      });
    }

    return trackingNumbers;
  }
}

export const jtlService = new JTLService();
export default jtlService;
